import { useEffect, useState } from 'react';
import { Sun, Moon, Monitor } from 'lucide-react';
import {
  getThemePreference,
  setThemePreference,
  watchSystemTheme,
  type ThemePreference,
} from './theme';

const OPTIONS: Array<{ value: ThemePreference; label: string; icon: typeof Sun }> = [
  { value: 'light', label: 'Light theme', icon: Sun },
  { value: 'dark', label: 'Dark theme', icon: Moon },
  { value: 'system', label: 'Follow system theme', icon: Monitor },
];

/** Light / dark / system segmented control. */
export default function ThemeToggle({ className = '' }: { className?: string }) {
  // Start from 'system' (what the prerendered HTML shows) and read the stored
  // preference after mount, so hydration markup matches.
  const [preference, setPreference] = useState<ThemePreference>('system');

  useEffect(() => {
    setPreference(getThemePreference());
    return watchSystemTheme(getThemePreference);
  }, []);

  const select = (value: ThemePreference) => {
    setPreference(value);
    setThemePreference(value);
  };

  return (
    <div
      role="radiogroup"
      aria-label="Colour theme"
      className={`flex items-center gap-0.5 rounded-full border border-lk-ink/15 p-0.5 ${className}`}
    >
      {OPTIONS.map(({ value, label, icon: Icon }) => {
        const active = preference === value;
        return (
          <button
            key={value}
            role="radio"
            aria-checked={active}
            aria-label={label}
            title={label}
            onClick={() => select(value)}
            className={`flex h-7 w-7 items-center justify-center rounded-full transition-colors ${
              active ? 'bg-lk-ink/10 text-lk-ink' : 'text-lk-ink/50 hover:text-lk-ink'
            }`}
          >
            <Icon className="h-3.5 w-3.5" />
          </button>
        );
      })}
    </div>
  );
}
